// src/padplanner/paperTheme.js
//
// Paper view's two sheets, Dark and Light (mapView.js keeps the viewer's
// choice), and every color that changes with them. THE ONE PLACE: the map's
// sheet and grid (PadPlannerMap.jsx, paperGridLayer.js), the layout's ink,
// selection and zones (FootprintEditor setDrawingStyle), the halos and
// witness lines (ChecksLayers setDrawingStyle) and the perimeter all read
// a tone from here. Off Paper none of it applies: Imagery and Basemap keep
// the map's own colors.
//
// ── DARK ────────────────────────────────────────────────────────────────────
//
// Today's Paper, unchanged: each value is the literal the map, the editor
// and ChecksLayers draw with, so choosing Dark draws what Paper always drew.
//
// ── LIGHT: ENGINEERING PAPER, AS THE REPORT PRINTS IT ───────────────────────
//
// The sheet, ink, grid and line colors are the S7 report's Paper figure's
// (admin-api/lib/padplanner/report/paperMap.js PAPER; paperMap.test.mjs holds
// the two equal), so a plan reads the same on screen and on paper: a
// near-white sheet, a blue-grey grid (minor at 0.22, major at 0.5, as the
// report draws it) and slate ink. The rest are darker steps of the dark
// sheet's hues, because the dark sheet's own fail on white (contrast against
// #FAF8F2, WCAG; 3:1 is the floor for lines and marks):
//
//   selection    #22d3ee 1.7  ->  #0e7490 5.0   (the handles' stroke already)
//   perimeter    #38bdf8 2.0  ->  #0284c7 3.9   (its outline; the fill stays the
//                                              report's #38bdf8 at 0.12, so the
//                                              sheet inside the pad is the print's)
//   zones        #f472b6 2.5  ->  #db2777 4.3
//   halos        #f97316 2.6  ->  #c2410c 4.9
//   untyped line #fbbf24 1.6  ->  #b45309 4.7
//   deferred     #f8fafc 1.0  ->  the ink
//
// The witness red (#ef4444, 3.5) and the symbols' hp and lp (symbols.js
// SYMBOL_COLORS) are the same on both sheets. Labels, dimension chips and the
// HUD panels carry their own dark backgrounds and need nothing.
import { LINE_COLORS, LINE_COLORS_LIGHT_PAPER } from "./lineVocabulary.js";

export const PAPER_TONES = Object.freeze(["dark", "light"]);
export const PAPER_TONE_LABELS = Object.freeze({ dark: "Dark", light: "Light" });
export const DEFAULT_PAPER_TONE = "dark";

export const PAPER_THEMES = Object.freeze({
  dark: Object.freeze({
    sheet: "#0D131B",          // a step above --bg
    ink: "#E2E8F0",            // footprint outlines, symbol outline and detail
    dot: "#93A6B8",            // == --text-3
    line: "#6E8296",           // == --text-4
    dotAlpha: 0.75, minorAlpha: 0.22, majorAlpha: 0.5,
    lineColors: LINE_COLORS,
    lineDefault: "#fbbf24",
    select: "#22d3ee",
    perimeter: "#38bdf8",
    perimeterFill: "#38bdf8",
    zone: "#f472b6",
    redzone: "#f97316",
    witness: "#ef4444",
    deferred: "#f8fafc",
  }),
  light: Object.freeze({
    sheet: "#FAF8F2",
    ink: "#1E293B",
    dot: "#64748B",
    line: "#94A3B8",
    dotAlpha: 0.75, minorAlpha: 0.22, majorAlpha: 0.5,
    lineColors: LINE_COLORS_LIGHT_PAPER,
    lineDefault: "#b45309",
    select: "#0e7490",
    perimeter: "#0284c7",
    perimeterFill: "#38bdf8",   // paperMap.js PAPER.perimeter
    zone: "#db2777",
    redzone: "#c2410c",
    witness: "#ef4444",
    deferred: "#1E293B",
  }),
});

/** A tone's theme; anything that is not a tone is Dark. */
export const paperTheme = (tone) => PAPER_THEMES[PAPER_TONES.includes(tone) ? tone : DEFAULT_PAPER_TONE];
